import { IconButton, Stack, Text } from '@fluentui/react';
import { Button, buttonStylesPrimary, useTheme } from '@h2oai/ui-kit';

import { FormattedUsageDataPoint } from '../types';
import { TimeSeriesStacked, UnitType } from './TimeSeriesStacked';

interface ChartContainerProps {
  data: FormattedUsageDataPoint[];
  groups: string[];
  isLoading: boolean;
  error?: Error;
  onRetry?: () => void;
  onRefresh?: () => void;
  groupColors?: Record<string, string>;
  unitType?: UnitType;
}

/**
 * ChartContainer component - Wraps the chart with error handling and loading states
 *
 * Displays:
 * - Loading message while data is being fetched
 * - Error message with retry button if fetch fails
 * - Chart when data is successfully loaded
 */
export function ChartContainer({
  data,
  groups,
  isLoading,
  error,
  onRetry,
  onRefresh,
  groupColors,
  unitType,
}: ChartContainerProps) {
  const theme = useTheme();
  return (
    <div style={{ height: 440, background: 'white', padding: '16px 24px 72px 24px', borderRadius: 4 }}>
      {/* Header bar with refresh button */}
      {onRefresh && (
        <Stack horizontal horizontalAlign="end">
          <IconButton
            iconProps={{ iconName: 'Refresh' }}
            title="Refresh chart data"
            ariaLabel="Refresh chart data"
            onClick={onRefresh}
            disabled={isLoading}
            styles={{ icon: { color: theme.palette?.black } }}
          />
        </Stack>
      )}
      {error ? (
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          styles={{ root: { height: '100%', textAlign: 'center' } }}
          tokens={{ childrenGap: 16 }}
        >
          <Text as="p" variant="large" styles={{ root: { color: theme.palette?.red600 } }}>
            Failed to load usage data
          </Text>
          <Text as="p" variant="medium" styles={{ root: { color: theme.palette?.black } }}>
            {error.message || 'An unexpected error occurred while fetching platform usage data.'}
          </Text>
          {onRetry && <Button styles={buttonStylesPrimary} text="Retry" onClick={onRetry} />}
        </Stack>
      ) : (
        <TimeSeriesStacked
          data={data}
          groups={groups}
          loadingMessage={isLoading ? 'Calculating usage...' : undefined}
          showLegend={false}
          groupColors={groupColors}
          unitType={unitType}
        />
      )}
    </div>
  );
}
